import { Stack, Text } from "@certn/cascadia";
import { Checkbox } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useField } from "formik";
import { useId } from "react";
import { useTranslation } from "react-i18next";

import { NotificationChannel } from "../../OrderContext";

interface NotificationMethodsFieldProps {
  smsEnabled: boolean;
  whatsappEnabled: boolean;
}

export function NotificationMethodsField({
  smsEnabled,
  whatsappEnabled,
}: NotificationMethodsFieldProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const descriptionId = useId();
  const [field, , helpers] = useField<NotificationChannel[] | undefined>(
    "notification_channels"
  );
  const channels = field.value ?? [];

  const toggle = (channel: NotificationChannel, selected: boolean) => {
    if (selected) {
      if (!channels.includes(channel)) {
        helpers.setValue([...channels, channel]);
      }
      return;
    }
    helpers.setValue(channels.filter((item) => item !== channel));
  };

  return (
    <section data-testid="notification-methods">
      <Stack gap="space-3">
        <h3 id={titleId} className={styles.heading}>
          <Text style="global/heading-medium">{t("Notification methods")}</Text>
        </h3>
        <span id={descriptionId}>
          <Text style="input/helper" color="text-weak">
            {t(
              "Invitations are always sent by email. When selected, SMS and WhatsApp methods are also used if a mobile number is provided."
            )}
          </Text>
        </span>
        <div
          className={styles.channels}
          role="group"
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
        >
          <Checkbox isSelected isDisabled testID="notification-channel-email">
            {t("Email (required)")}
          </Checkbox>
          {smsEnabled && (
            <Checkbox
              testID="notification-channel-sms"
              isSelected={channels.includes("SMS")}
              onChange={(selected) => toggle("SMS", selected)}
            >
              {t("SMS")}
            </Checkbox>
          )}
          {whatsappEnabled && (
            <Checkbox
              testID="notification-channel-whatsapp"
              isSelected={channels.includes("WHATSAPP")}
              onChange={(selected) => toggle("WHATSAPP", selected)}
            >
              {t("WhatsApp")}
            </Checkbox>
          )}
        </div>
        <Text style="form/body-secondary">
          {t(
            "By providing a candidate's phone number, you confirm that you have the legal authority to do so and that any required consent has been obtained in accordance with applicable law. Candidates may opt out of SMS notifications at any time."
          )}
        </Text>
      </Stack>
    </section>
  );
}

const styles = {
  heading: css({ margin: 0 }),
  channels: css({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "var(--space-6)",
  }),
};
